import {AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, Button} from '@tryghost/shade';
import {HeldAccountDeletion, accountDeletionApi} from '@/api/account-deletion';
import {useEffect, useState} from 'react';

export function AccountDeletionsPage() {
    const [jobs, setJobs] = useState<HeldAccountDeletion[]>([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState<HeldAccountDeletion | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [action, setAction] = useState<'release' | 'cancel'>('release');
    const [notice, setNotice] = useState('');

    useEffect(() => {
        let active = true;
        accountDeletionApi.list().then((data) => {
            if (active) {
                setJobs(data);
            }
        }).catch((err) => {
            if (active) {
                setError(err instanceof Error ? err.message : 'Could not load deletion requests.');
            }
        }).finally(() => {
            if (active) {
                setLoading(false);
            }
        });
        return () => {
            active = false;
        };
    }, []);

    const submit = async () => {
        if (!selected || busy) {
            return;
        }
        setBusy(true);
        setError('');
        try {
            await accountDeletionApi[action](selected.id);
            setJobs(current => current.filter(job => job.id !== selected.id));
            setSelected(null);
            setNotice(action === 'release' ? 'Deletion released. Cleanup will continue in the background.' : 'Deletion request cancelled. Account status is unchanged.');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not update this request.');
        } finally {
            setBusy(false);
        }
    };

    return <section className="space-y-6">
        <h1>Account deletions</h1>
        <p>Review all billing processors and stop every charge before releasing a request. Inventory reflects current control-plane records; check the processors too.</p>
        {error && !selected && <p role="alert">{error}</p>}
        {notice && <p role="status">{notice}</p>}
        {loading ? <p role="status">Loading requests…</p> : !jobs.length && !error ? <p>No account deletions awaiting review.</p> : null}
        {jobs.map(job => <article key={job.id} className="space-y-3 rounded border p-4">
            <h2>{job.email}</h2>
            <p>Requested: <time dateTime={job.requested_at}>{new Date(job.requested_at).toLocaleString()}</time></p>
            <p>Job: {job.id}</p>
            <h3>Subscriptions</h3>
            <ul>{job.inventory.subscriptions.map(row => <li key={row.id}>{row.processor} — {row.status} — {row.paykickstart_subscription_id || row.paykickstart_invoice_id || row.paykickstart_transaction_id || row.id}</li>)}</ul>
            <h3>Theme entitlements</h3>
            <ul>{job.inventory.themes.map(row => <li key={row.id}>{row.id} — {row.status} — {row.paykickstart_subscription_id || row.paykickstart_invoice_id || row.paykickstart_transaction_id || 'No PKS payment reference'}</li>)}</ul>
            <h3>Stripe connections</h3>
            <ul>{job.inventory.stripe.map(row => <li key={`${row.tenant_id}:${row.stripe_account_id}`}>{row.tenant_id} — {row.stripe_account_id} — {row.onboarding_state} — {row.livemode ? 'Live' : 'Test'}</li>)}</ul>
            <Button disabled={busy} variant="destructive" onClick={() => {
                setError('');
                setNotice('');
                setAction('release');
                setSelected(job);
            }}>Release</Button>
            <Button disabled={busy} variant="outline" onClick={() => {
                setError('');
                setNotice('');
                setAction('cancel');
                setSelected(job);
            }}>Cancel request</Button>
        </article>)}
        <AlertDialog open={!!selected} onOpenChange={(open) => {
            if (!open && !busy) {
                setSelected(null);
            }
        }}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>{action === 'release' ? 'Release account deletion?' : 'Cancel deletion request?'}</AlertDialogTitle>
                    <AlertDialogDescription>{action === 'release'
                        ? `Confirm you have stopped all billing for ${selected?.email}. This immediately disables the account and starts permanent deletion.`
                        : `Cancel the deletion request for ${selected?.email}. Account status is unchanged and the deletion hold on purchases is removed.`}</AlertDialogDescription>
                </AlertDialogHeader>
                {error && <p role="alert">{error}</p>}
                <AlertDialogFooter>
                    <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
                    <Button disabled={busy} variant="destructive" onClick={() => void submit()}>{busy ? 'Please wait…' : action === 'release' ? 'Confirm release' : 'Confirm cancellation'}</Button>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    </section>;
}
